import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Brain, Loader, Search, Twitter, Users, GitCompare, RefreshCw, ChevronDown } from 'lucide-react';
import { getTensorpulseMindshare, getTensorpulseMindshareHistory, getLastUpdateTimestamp } from '../services/tensorpulseMindshareService';
import { MindshareChart } from '../components/MindshareChart';
import { CompareView } from '../components/mindshare/CompareView';
import type { SubnetMindshare } from '../types/mindshare';
import type { GainerData } from '../services/gainersService';
import { getHolderGainers, getTwitterGainers, getVolumeGainers } from '../services/gainersService';
import { rateLimiter } from '../services/rateLimiter';
import { autoUpdateService } from '../services/autoUpdateService';
import { getAllCurrentMindshare, getTopGainers, getTopLosers } from '../services/currentMindshareService';
import { formatDistanceToNow } from 'date-fns';

export default function TensorPulseMindshare() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mindshareData, setMindshareData] = useState<SubnetMindshare[]>([]);
  const [mindshareLoading, setMindshareLoading] = useState(true);
  const [activeView, setActiveView] = useState<'chart' | 'compare'>('chart');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [holderGainers, setHolderGainers] = useState<GainerData[]>([]);
  const [twitterGainers, setTwitterGainers] = useState<GainerData[]>([]);
  const [volumeGainers, setVolumeGainers] = useState<GainerData[]>([]);
  const [gainerDataLoading, setGainerDataLoading] = useState(true);
  const [percentChanges, setPercentChanges] = useState<Record<number, number>>({});
  const [isOffline, setIsOffline] = useState(false);
  const [subnetHistories, setSubnetHistories] = useState<Record<number, any>>({});
  const [dataInitialized, setDataInitialized] = useState(false);
  const [topGainers, setTopGainers] = useState<any[]>([]);
  const [topLosers, setTopLosers] = useState<any[]>([]);
  const [nextUpdateTime, setNextUpdateTime] = useState<string>('');
  const [itemCount, setItemCount] = useState<number>(10);

  // Check network status on mount
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    // Set initial state
    setIsOffline(!navigator.onLine);
    
    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Sync with rate limiter's offline mode
    setIsOffline(rateLimiter.isOffline());
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update the next update time display
  useEffect(() => {
    if (!lastUpdated) return;
    
    // Calculate the next update time (top of the next hour)
    const nextUpdate = new Date(lastUpdated);
    nextUpdate.setHours(nextUpdate.getHours() + 1);
    nextUpdate.setMinutes(0);
    nextUpdate.setSeconds(0);
    nextUpdate.setMilliseconds(0);
    
    // Format the time until next update
    const updateTimeString = formatDistanceToNow(nextUpdate, { addSuffix: true });
    setNextUpdateTime(updateTimeString);
    
    // Update the countdown every minute
    const interval = setInterval(() => {
      const updatedTimeString = formatDistanceToNow(nextUpdate, { addSuffix: true });
      setNextUpdateTime(updatedTimeString);
    }, 60000);
    
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const loadGainerData = useCallback(async () => {
    try {
      setGainerDataLoading(true);
      
      const [holders, twitter, volume, gainers, losers] = await Promise.all([
        getHolderGainers(),
        getTwitterGainers(),
        getVolumeGainers(),
        getTopGainers(5),
        getTopLosers(5)
      ]);
      
      setHolderGainers(holders);
      setTwitterGainers(twitter);
      setVolumeGainers(volume);
      setTopGainers(gainers);
      setTopLosers(losers);
    } catch (error) {
      console.error('Error fetching gainer data:', error);
    } finally {
      setGainerDataLoading(false);
    }
  }, []);

  const loadMindshareData = useCallback(async () => {
    try {
      setMindshareLoading(true);
      
      // Get TensorPulse mindshare data
      const data = await getTensorpulseMindshare();
      
      if (data.length > 0) {
        setMindshareData(data);
        
        // Get percent changes from current_mindshare collection
        const currentMindshareEntries = await getAllCurrentMindshare();
        const changes: Record<number, number> = {};
        
        currentMindshareEntries.forEach(entry => {
          if (entry.percentChange !== undefined) {
            changes[entry.netuid] = entry.percentChange;
          }
        });
        
        setPercentChanges(changes);
        
        // Get the last update timestamp
        const timestamp = await getLastUpdateTimestamp();
        setLastUpdated(timestamp || new Date());
      }
    } catch (err) {
      console.error('Failed to load TensorPulse mindshare data:', err);
    } finally {
      setMindshareLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only load data if we haven't initialized yet
    if (!dataInitialized) {
      const loadInitialData = async () => {
        try {
          setLoading(true);
          await Promise.all([
            loadMindshareData(),
            loadGainerData()
          ]);
          setDataInitialized(true);
        } finally {
          setLoading(false);
        }
      };

      loadInitialData();
    }
  }, [loadMindshareData, loadGainerData, dataInitialized, isRefreshing]);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      
      // Force a complete data refresh including history
      await autoUpdateService.forceUpdate();
      
      // Reload our data
      await loadMindshareData();
      await loadGainerData();
      
      // Load new subnet histories for the top subnets
      const topSubnets = mindshareData.slice(0, 5);
      const historyPromises = topSubnets.map(subnet => getTensorpulseMindshareHistory(subnet.netuid));
      const historyData = await Promise.all(historyPromises);
      
      const newSubnetHistories = { ...subnetHistories };
      historyData.forEach((history, index) => {
        if (history) {
          newSubnetHistories[topSubnets[index].netuid] = history;
        }
      });
      
      setSubnetHistories(newSubnetHistories);
      setDataInitialized(true);
      
      // Clear localStorage cache
      if (typeof window !== 'undefined') {
        // Clear all mindshare-related cache items
        Object.keys(localStorage).forEach(key => {
          if (key.includes('mindshare') || key.includes('cache') || key.includes('tensorpulse')) {
            localStorage.removeItem(key);
          }
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        {lastUpdated && (
          <div className="text-xs text-gray-400">
            <span>Last updated: {lastUpdated.toLocaleString()}</span>
            <span className="ml-2">Next update: {nextUpdateTime}</span>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          {isOffline && (
            <div className="px-2 py-1 rounded-lg bg-yellow-600/70 text-white text-xs flex items-center">
              <span>Offline Mode</span>
            </div>
          )}
          
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isOffline}
            className={`px-2 py-1 rounded-lg flex items-center space-x-1 text-xs ${
              isRefreshing || isOffline
                ? 'bg-black/40 text-gray-500 cursor-not-allowed' 
                : 'bg-cyan-600 hover:bg-cyan-700 text-white'
            }`}
          >
            <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          
          <button
            onClick={() => setActiveView('chart')}
            className={`px-2 py-1 rounded-lg flex items-center space-x-1 text-xs ${
              activeView === 'chart' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-black/20 text-gray-300 hover:bg-black/30'
            }`}
          >
            <span>Chart</span>
          </button>
          <button
            onClick={() => setActiveView('compare')}
            className={`px-2 py-1 rounded-lg flex items-center space-x-1 text-xs ${
              activeView === 'compare' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-black/20 text-gray-300 hover:bg-black/30'
            }`}
          >
            <GitCompare className="w-3 h-3" />
            <span>Compare</span>
          </button>
        </div>
      </div>
      
      {activeView === 'compare' ? (
        <CompareView 
          mindshareData={mindshareData} 
          loading={mindshareLoading} 
          subnetHistories={subnetHistories}
        />
      ) : (
        <>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full">
              <MindshareChart 
                mindshareData={mindshareData} 
                loading={mindshareLoading} 
                percentChanges={percentChanges}
                onRefresh={handleRefresh}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}